import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { StatusBadge } from "../components/ProductCard";
import { supabase } from "../lib/supabase";
import { Star, Check, ChevronRight, Loader2 } from "lucide-react";

const DURATIONS = [
  { key: "daily", label: "24 Hours", priceKey: "price_daily" },
  { key: "weekly", label: "7 Days", priceKey: "price_weekly" },
  { key: "monthly", label: "30 Days", priceKey: "price_monthly", recommended: true }
];

const StarRating = ({ rating, size = "w-5 h-5" }) => (
  <div className="flex">
    {[1, 2, 3, 4, 5].map((star) => (
      <Star
        key={star}
        className={`${size} ${star <= rating ? "text-[#F5A623] fill-[#F5A623]" : "text-[#27272A]"}`}
      />
    ))}
  </div>
);

const FeatureItem = ({ feature }) => (
  <div className="flex items-center gap-2 text-[#A1A1AA]">
    <Check className="w-4 h-4 text-[#4F7CFF]" />
    <span>{feature}</span>
  </div>
);

const DurationOption = ({ duration, selected, price, onSelect }) => (
  <button
    onClick={() => onSelect(duration.key)}
    className={`duration-option w-full flex items-center justify-between ${selected ? "selected" : ""}`}
    data-testid={`duration-${duration.key}`}
  >
    <div className="flex items-center gap-3">
      <div className={`w-4 h-4 border ${selected ? "border-[#4F7CFF] bg-[#4F7CFF]" : "border-white/30"}`}>
        {selected && <Check className="w-3 h-3 text-white" />}
      </div>
      <span className="text-white">{duration.label}</span>
      {duration.recommended && (
        <span className="px-2 py-0.5 bg-[#4F7CFF]/10 text-[#4F7CFF] text-xs uppercase">Best Value</span>
      )}
    </div>
    <span className="price-tag text-[#4F7CFF] text-xl">${price ?? 0}</span>
  </button>
);

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDuration, setSelectedDuration] = useState("monthly");

  const fetchData = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .eq("id", id)
        .single();

      if (error || !data) {
        navigate("/products");
        return;
      }

      setProduct(data);
    } catch (err) {
      console.error(err);
      navigate("/products");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050508] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#4F7CFF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) return null;

  const safeFeatures = Array.isArray(product.features)
    ? product.features
    : [
        "Fast delivery",
        "Reliable support",
        "Discord ticket fulfillment"
      ];

  const selectedDurationConfig = DURATIONS.find((d) => d.key === selectedDuration) || DURATIONS[2];
  const selectedPrice = product[selectedDurationConfig.priceKey] ?? 0;

  return (
    <div className="min-h-screen bg-[#050508]" data-testid="product-detail-page">
      <Navbar />

      <main className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-sm text-[#A1A1AA] mb-8">
            <Link to="/products" className="hover:text-white">Products</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">{product.name}</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <div className="relative mb-6 overflow-hidden">
                <img
                  src={product.image_url || "https://images.pexels.com/photos/33888376/pexels-photo-33888376.jpeg"}
                  alt={product.name}
                  className="w-full h-80 object-cover opacity-70"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050508] to-transparent" />
                <div className="absolute top-4 right-4">
                  <StatusBadge status={product.status} />
                </div>
              </div>

              <p className="text-[#A1A1AA] text-sm uppercase tracking-wider mb-2">
                Premium Product
              </p>

              <h1 className="font-heading text-3xl sm:text-4xl text-white mb-4">
                {product.name}
              </h1>

              <div className="flex items-center gap-2 mb-6">
                <StarRating rating={5} />
                <span className="text-[#A1A1AA]">New Listing</span>
              </div>

              <p className="text-[#A1A1AA] mb-6">
                {product.description || "No description available."}
              </p>

              <div className="mb-8">
                <h3 className="font-heading text-lg text-white mb-4">Features</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {safeFeatures.map((feature, idx) => (
                    <FeatureItem key={idx} feature={feature} />
                  ))}
                </div>
              </div>
            </div>

            <div>
              <div className="bg-[#0a0a12] border border-white/5 p-6 sticky top-24">
                <h3 className="font-heading text-xl text-white mb-6">Select Duration</h3>

                <div className="space-y-3 mb-6">
                  {DURATIONS.map((dur) => (
                    <DurationOption
                      key={dur.key}
                      duration={dur}
                      selected={selectedDuration === dur.key}
                      price={product[dur.priceKey] ?? 0}
                      onSelect={setSelectedDuration}
                    />
                  ))}
                </div>

                <a
                  href="https://discord.gg/efeySGKGJk"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="purchase-btn"
                >
                  Join Discord to Purchase <span className="price-tag">${selectedPrice}</span>
                </a>

                {product.status === "detected" && (
                  <p className="text-[#FF3B30] text-sm text-center mt-4">
                    This product is currently detected. Please wait for an update.
                  </p>
                )}

                <div className="mt-6 bg-black/40 border border-white/5 p-4 text-sm text-[#A1A1AA]">
                  <p className="mb-2 text-white font-medium">Delivery Flow</p>
                  <p>Buy → Join Discord → Open ticket → Receive access</p>
                </div>

                <p className="text-[#A1A1AA] text-sm text-center mt-4">
                  Delivery is handled through Discord tickets
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProductDetailPage;